import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faCircleArrowDown } from '@fortawesome/pro-solid-svg-icons';
import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { CollectionArrow } from '@sorare/core/src/atoms/icons/CollectionArrow';
import { LineupBonuses } from '@sorare/core/src/atoms/icons/LineupBonuses';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BrandXXS, LabelL } from '@sorare/core/src/atoms/typography';
import { InGameCurrencyBalance } from '@sorare/core/src/components/balance/InGameCurrencyBalance';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { inGameCurrencyToRarity } from '@sorare/core/src/lib/scarcity';

import { ComposeBonuses } from 'components/leaderboard/ComposeBonuses';
import { bonusRatingMessages, colorPerBonusRating } from 'lib/bonus';

import { AppearanceAvatar } from './AppearanceAvatar';
import { AppearanceBonusBreakdown } from './AppearanceBonusBreakdown';
import type { LineupBonusDetails_so5Lineup } from './__generated__/index.graphql';

const Root = styled.div`
  height: 100%;
  flex: 1;
  display: grid;
  grid-template-rows: auto min-content;
`;
const Content = styled(FullWidthContainer)`
  overflow: auto;
  padding-bottom: var(--double-unit);
  display: grid;
  grid-template-rows: min-content auto;
`;
const Footer = styled(FullWidthContainer)`
  border-top: 1px solid var(--c-nd-150);
  padding-top: var(--double-unit);
  padding-bottom: var(--triple-unit);
  overflow: hidden;
  max-width: 100%;
`;
const AppearanceWrapper = styled(Vertical)`
  gap: var(--unit);
`;
const Grid = styled.div`
  display: grid;
  padding: var(--unit);
  grid-template-columns: repeat(var(--nb-appearances), 1fr);

  gap: var(--intermediate-unit);
  &:has(${AppearanceWrapper}:nth-child(7)) {
    gap: 0;
  }
  overflow-x: auto;
  max-width: 100%;
  width: 100%;
`;
const FullHeightWrapper = styled.div`
  flex: 1;
`;

type Props = { so5Lineup: LineupBonusDetails_so5Lineup };

export const LineupBonusDetails = ({ so5Lineup }: Props) => {
  const {
    so5: { inSeasonName, cardMaxGrade },
  } = useConfigContext();
  const { currentUser } = useCurrentUserContext();
  const { xpBalances = [] } = currentUser || {};

  const [selectedAppearanceIndex, setSelectedAppearanceIndex] = useState<
    number | undefined
  >(undefined);

  const bonusRatingColor = so5Lineup.bonusRating
    ? colorPerBonusRating[so5Lineup.bonusRating]
    : undefined;

  const selectedAppearance =
    typeof selectedAppearanceIndex === 'number'
      ? so5Lineup.so5Appearances.at(selectedAppearanceIndex)
      : null;

  const xpBalance = xpBalances.find(
    ({ currency }) =>
      inGameCurrencyToRarity[currency] === selectedAppearance?.rarity
  );

  return (
    <Root>
      <Content>
        <SBHorizontal>
          {!!xpBalance && (
            <InGameCurrencyBalance
              currency={xpBalance.currency}
              amount={xpBalance.amount}
            />
          )}
        </SBHorizontal>
        {selectedAppearance ? (
          <AppearanceBonusBreakdown
            key={selectedAppearance.id}
            so5Appearance={selectedAppearance}
            cardMaxGrade={cardMaxGrade || 0}
            seasonalityName={inSeasonName}
          />
        ) : (
          <Vertical>
            <FullHeightWrapper>
              <ComposeBonuses
                customTitle={
                  <Horizontal>
                    <LineupBonuses size={2} />
                    <LabelL>
                      <FormattedMessage
                        id="LineupBonusDrawer.lineupBonusesTitle"
                        defaultMessage="Lineup Bonuses"
                      />
                    </LabelL>
                  </Horizontal>
                }
                lineupBonuses={so5Lineup.bonuses}
                inline
              />
            </FullHeightWrapper>
            <Vertical gap={0.5}>
              <BodyM color="var(--c-blue-300)" className="text-center">
                <FormattedMessage
                  id="LineupBonusDrawer.pickAPlayer"
                  defaultMessage="Pick a player to view their bonus"
                />
              </BodyM>
              <FontAwesomeIcon
                color="var(--c-blue-300)"
                icon={faCircleArrowDown}
              />
            </Vertical>
          </Vertical>
        )}
      </Content>
      <Footer>
        <Vertical gap={1.5}>
          {!!so5Lineup.bonusRating && (
            <Horizontal>
              <CollectionArrow color={bonusRatingColor} />
              <LabelL bold>
                <Horizontal>
                  {typeof so5Lineup.bonusPoints === 'number' ? (
                    <FormattedMessage
                      id="LineupBonusDetails.overallCardBonuses"
                      defaultMessage="Overall Card Bonuses: <span>{rating}</span>"
                      values={{
                        span: (...chunks: ReactNode[]) => (
                          <Horizontal
                            gap={0.5}
                            style={{
                              color: bonusRatingColor,
                            }}
                          >
                            {chunks}
                          </Horizontal>
                        ),
                        rating: (
                          <FormattedMessage
                            {...fantasy.ordinalPoints}
                            values={{
                              score: (
                                <FormattedNumber
                                  value={so5Lineup.bonusPoints || 0}
                                  maximumFractionDigits={1}
                                  signDisplay="always"
                                />
                              ),
                              sup: (...chunks: ReactNode[]) => (
                                <BrandXXS bold>{chunks}</BrandXXS>
                              ),
                            }}
                          />
                        ),
                      }}
                    />
                  ) : (
                    <FormattedMessage
                      id="LineupBonusDetails.teamBonusRating"
                      defaultMessage="Team Bonus Rating: <span>{rating}</span>"
                      values={{
                        span: (...chunks: ReactNode[]) => (
                          <span
                            style={{
                              color: bonusRatingColor,
                            }}
                          >
                            {chunks}
                          </span>
                        ),
                        rating: (
                          <FormattedMessage
                            {...bonusRatingMessages[so5Lineup.bonusRating]}
                          />
                        ),
                      }}
                    />
                  )}
                </Horizontal>
              </LabelL>
            </Horizontal>
          )}
          <Grid
            style={
              {
                '--nb-appearances': so5Lineup.so5Appearances.length,
              } as CSSProperties
            }
          >
            {so5Lineup.so5Appearances.map((so5Appearance, i) => (
              <AppearanceWrapper key={so5Appearance.id}>
                <AppearanceAvatar
                  so5Appearance={so5Appearance}
                  selected={selectedAppearanceIndex === i}
                  onClick={() => {
                    setSelectedAppearanceIndex(i);
                  }}
                />
              </AppearanceWrapper>
            ))}
          </Grid>
        </Vertical>
      </Footer>
    </Root>
  );
};

LineupBonusDetails.fragments = {
  so5Lineup: gql`
    fragment LineupBonusDetails_so5Lineup on So5Lineup {
      id
      bonusRating
      bonusPoints
      bonuses {
        ...ComposeBonuses_so5AppearanceBonusInterface
      }
      so5Appearances {
        id
        rarity
        ...AppearanceBonusBreakdown_so5Appearance
        ...AppearanceAvatar_so5Appearance
      }
    }
    ${AppearanceAvatar.fragments.so5Appearance}
    ${AppearanceBonusBreakdown.fragments.so5Appearance}
    ${ComposeBonuses.fragments.so5AppearanceBonusInterface}
  ` as TypedDocumentNode<LineupBonusDetails_so5Lineup>,
};
